import { Component, Element, Event, EventEmitter, Host, h, Method, Prop, State, Watch } from '@stencil/core';
import { intl } from '../../utils/i18n';
import { SSearchData, SClearData, SKeyUpData, SInputData } from '../../types/types';
import shadowRootParentOrDocument from '../../utils/generic-functions';
import parseJson from '../../utils/parse-json';

@Component({
  tag: 's-input-extended',
  styleUrl: 's-input-extended.scss',
  shadow: false,
})
export class SInputExtended {
  @Element() element: HTMLSInputExtendedElement;

  @Prop({ mutable: true }) type: string;

  @Prop({ attribute: 'inputid' }) inputId = '';

  @Prop({ mutable: true }) value?: string;

  @Prop() placeholder?: string;

  @Prop({ attribute: 'autofocus' }) autoFocus?: boolean = false;

  @Prop({ attribute: 'i18n' }) i18n?: Record<string, string> | string = {};

  @State() showClearButton = false;

  @State() i18nJson: Record<string, string> = {};

  @Watch('value')
  watchValue(newValue: string) {
    newValue ? (this.showClearButton = true) : (this.showClearButton = false);
  }

  @Watch('i18n')
  watchI18n() {
    this.i18nJson = parseJson(this.i18n, "Please provide valid JSON string on input-extended's i18n attribute", {});
  }

  @Event({
    eventName: 's-clear',
    bubbles: true,
    composed: true,
  })
  sClear: EventEmitter<SClearData>;

  @Event({
    eventName: 's-search',
    bubbles: true,
    composed: true,
  })
  sSearch: EventEmitter<SSearchData>;

  @Event({
    eventName: 's-keyup',
    bubbles: true,
    composed: true,
  })
  sKeyUp: EventEmitter<SKeyUpData>;

  @Event({
    eventName: 's-input',
    bubbles: true,
    composed: true,
  })
  sInput: EventEmitter<SInputData>;

  @Method()
  async getValue() {
    return this.#input.value;
  }

  connectedCallback() {
    this.watchValue(this.value);
    this.watchI18n();
  }

  get #inputExtendedText() {
    const ids = {
      placeHolder: 'inputExtended.placeholder',
      ariaLabelClear: 'inputExtended.ariaLabel.clear',
      ariaLabelSearch: 'inputExtended.ariaLabel.search',
    };

    return {
      placeholder: intl.formatMessage({ id: ids.placeHolder, defaultMessage: this.i18nJson[ids.placeHolder] || 'Search' }),
      ariaLabelClear: intl.formatMessage({ id: ids.ariaLabelClear, defaultMessage: this.i18nJson[ids.ariaLabelClear] || 'clear input' }),
      ariaLabelSearch: intl.formatMessage({ id: ids.ariaLabelSearch, defaultMessage: this.i18nJson[ids.ariaLabelSearch] || 'search' }),
    };
  }

  get #input() {
    return this.element.querySelector('input');
  }

  #handleSearchInputChange() {
    if (this.#input.value) {
      this.showClearButton = true;
    } else {
      this.showClearButton = false;
    }
  }

  #handleInputKeyUp(e: KeyboardEvent) {
    if (e.key === 'Enter') {
      this.#handleSearchButtonClick();
      e.preventDefault();
    }
    this.sKeyUp.emit({ inputId: this.inputId, event: e, value: e.key });
    this.sInput.emit({ inputId: this.inputId, value: this.#input.value });
  }

  #handleSearchButtonClick() {
    const searchValue = this.#input.value;
    this.sSearch.emit({ inputId: this.inputId, value: searchValue });
  }

  #handleClearButtonClick() {
    this.#input.value = '';
    this.watchValue('');
    this.sClear.emit({ inputId: this.inputId });
  }

  #inputHtml() {
    return (
      <input
        aria-label={shadowRootParentOrDocument(this.element).querySelector(`label[for='${this.inputId}']`) ? null : this.element.getAttribute('aria-label')}
        id={this.inputId}
        placeholder={this.placeholder || this.#inputExtendedText.placeholder}
        value={this.value}
        onInput={() => this.#handleSearchInputChange()}
        onPaste={() => this.#handleSearchInputChange()}
        onKeyUp={e => {
          this.#handleInputKeyUp(e);
        }}
        autofocus={this.autoFocus}
        aria-describedby={this.element.getAttribute('aria-describedby')}
      />
    );
  }

  #searchButtonHtml() {
    return (
      <button
        class="icon ghost"
        data-ref="primary-action-button"
        aria-label={this.#inputExtendedText.ariaLabelSearch}
        type="button"
        onClick={() => this.#handleSearchButtonClick()}
      >
        <s-icon name="search" aria-hidden="true"></s-icon>
      </button>
    );
  }

  #clearButtonHtml(ref: string) {
    const classList = `ghost icon ${this.showClearButton ? '' : 'hidden'}`;
    return (
      <button class={classList} data-ref={ref} aria-label={this.#inputExtendedText.ariaLabelClear} type="button" onClick={() => this.#handleClearButtonClick()}>
        <s-icon name="close" aria-hidden="true"></s-icon>
      </button>
    );
  }

  static decorativeIconHtml = () => {
    return (
      <div data-ref="decorative-icon-container">
        <s-icon name="search" aria-hidden="true" data-ref="decorative-icon"></s-icon>
      </div>
    );
  };

  #typeHtml() {
    if (this.type === 'search') {
      return (
        <div data-ref="input-wrapper">
          {this.#inputHtml()}
          {this.#searchButtonHtml()}
          {this.#clearButtonHtml('secondary-action-button')}
        </div>
      );
    }
    if (this.type === 'search-live') {
      return (
        <div data-ref="input-wrapper">
          {this.#inputHtml()}
          {SInputExtended.decorativeIconHtml()}
          {this.#clearButtonHtml('primary-action-button')}
        </div>
      );
    }

    return '';
  }

  render() {
    return <Host>{this.#typeHtml()}</Host>;
  }
}
